import Heading from '@tiptap/extension-heading';
import { Plugin } from 'prosemirror-state';
import { v4 as uuidv4 } from 'uuid';
import type { Heading as HeadingType } from '../data/.types/headings.ts';

const headingIdPlugin = (headingConfig: HeadingType) => {
  return new Plugin({
    appendTransaction: (_transactions, _oldState, newState) => {
      if (!headingConfig.allowAnchoredHeadings) return null;

      const transaction = newState.tr;
      let modified = false;

      newState.doc.descendants((node, pos) => {
        if (node.type.name === 'heading') {
          /**
           * For all heading nodes without an 'id' attribute at the
           * that should have one, add one.
           */
          if (
            !node.attrs.id &&
            headingConfig.allowedAnchoredHeadingLevels?.includes(
              node.attrs.level,
            )
          ) {
            transaction.setNodeMarkup(pos, undefined, {
              ...node.attrs,
              id: uuidv4(),
            });

            modified = true;
          } else if (
            /**
             * For all heading nodes with an 'id' attribute that
             * shouldn't have one, remove it.
             */
            node.attrs.id &&
            !headingConfig.allowedAnchoredHeadingLevels?.includes(
              node.attrs.level,
            )
          ) {
            const attrs = { ...node.attrs };
            attrs.id = undefined;

            transaction.setNodeMarkup(pos, undefined, {
              ...attrs,
            });

            modified = true;
          }
        }
      });

      return modified ? transaction : null;
    },
  });
};

const CustomHeading = (headingConfig: HeadingType) => {
  return Heading.extend({
    addProseMirrorPlugins() {
      const plugins = this.parent?.() || [];

      return [...plugins, headingIdPlugin(headingConfig)];
    },
    addAttributes() {
      return {
        ...this.parent?.(),
        id: {
          default: () => null,
          parseHTML: (element) => element.getAttribute('id'),
          renderHTML: (attributes) => {
            if (!attributes.id) {
              return {};
            }
            return {
              id: attributes.id,
            };
          },
        },
      };
    },
    renderHTML({ HTMLAttributes, ...options }) {
      const { level } = options.node.attrs;

      const existingAnchorId =
        'id' in HTMLAttributes ? (HTMLAttributes.id as string) : undefined;

      const allowedAnchoredHeadingLevels =
        headingConfig.allowedAnchoredHeadingLevels || [];

      const headingLevelIsAllowed =
        allowedAnchoredHeadingLevels.includes(level);

      if (headingConfig.allowAnchoredHeadings && headingLevelIsAllowed) {
        return [
          `h${level}`,
          {
            ...HTMLAttributes,
            id: existingAnchorId,
          },
          0,
        ];
      }

      return [`h${level}`, { ...HTMLAttributes }, 0];
    },
  });
};

export default CustomHeading;
